import classNames from "classnames";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { KeyDownWrapper } from "components/DesignSystem/KeydownWrapper/KeydownWrapper";
import { NoteCard } from "components/DesignSystem/Notes/NoteCard";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Form, Formik, FormikValues } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useToast } from "hooks/useToast";
import { useEffect, useRef } from "react";
import { OutlinedPaperPlaneRight } from "components/icons/OutlinedPaperPlaneRight";
import {
  COMMENT_TYPE,
  Note,
  useCreateNotesMutation,
  useGetNoteQuery,
} from "store/apis/notes";
import { BackendError } from "types/utils/error";

const EMPTY_NOTES: Note[] = [];

const NoteItem = ({
  note,
  lastNoteId,
}: {
  note: Note;
  lastNoteId?: string;
}) => {
  const commentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastNoteId === note.uuid) {
      commentRef?.current?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [lastNoteId, note.uuid]);

  return <NoteCard ref={commentRef} note={note} />;
};

export const JournalEntryComments = ({
  ledgerEntryId,
}: {
  ledgerEntryId: string;
}) => {
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );

  const { data } = useGetNoteQuery(
    {
      entityId,
      parentObjId: ledgerEntryId,
      page_num: 1,
      commentType: COMMENT_TYPE.LEDGER_ENTRY_COMMENT,
    },
    { skip: !entityId || !ledgerEntryId }
  );

  const [addComment, { isLoading }] = useCreateNotesMutation();

  const notes = data?.comments ?? EMPTY_NOTES;
  const lastNoteId = notes[notes.length - 1]?.uuid;
  const noComments = notes.length === 0;

  const onAddComment = async (
    values: { comment: string },
    { resetForm }: FormikValues
  ) => {
    try {
      await addComment({
        entityId,
        parentObjId: ledgerEntryId,
        payload: {
          description: values?.comment,
          comment_type: COMMENT_TYPE.LEDGER_ENTRY_COMMENT,
        },
      }).unwrap();
      resetForm();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Accordion.Root
      type="multiple"
      defaultValue={["COMMENT"]}
      className="t-flex t-gap-4 t-flex-col"
    >
      <Accordion.Item value="COMMENT">
        <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
          Comments {notes.length > 0 && `(${notes.length})`}
        </Accordion.Trigger>
        <Accordion.Content>
          <span>
            <KeyDownWrapper>
              <div className="t-overflow-auto t-flex t-flex-col t-gap-2 t-max-h-80">
                {!noComments && (
                  <div className="t-flex t-flex-col t-gap-2">
                    {notes.map((note) => (
                      <NoteItem
                        note={note}
                        key={note.uuid}
                        lastNoteId={lastNoteId}
                      />
                    ))}
                  </div>
                )}
                <Formik initialValues={{ comment: "" }} onSubmit={onAddComment}>
                  {() => (
                    <Form
                      className={classNames(
                        "t-w-full t-m-0 t-sticky t-bottom-0",
                        {
                          "t-pointer-events-none": !hasWritePermission,
                        }
                      )}
                    >
                      <TextInput
                        placeholder="Add your comment here"
                        name="comment"
                        customSize="small"
                        disabled={!hasWritePermission}
                        rightComponent={
                          <Button
                            customType="ghost_icon"
                            size="small"
                            isLoading={isLoading}
                            disabled={isLoading || !hasWritePermission}
                          >
                            <OutlinedPaperPlaneRight
                              size="16"
                              color="#706A85"
                            />
                          </Button>
                        }
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            </KeyDownWrapper>
          </span>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
